<div class="directory-companion-guided" data-test="guided-flow">
  @if ((canGoBack() && hasResults() && showBackButton()) || showCloseButton()) {
    <div class="header">
      @if (canGoBack() && hasResults() && showBackButton()) {
        <button type="button" class="back-link" data-test="guided-flow-back" (click)="onBack()">
          <iris-icon name="chevron_left" [size]="iconSizes.SM"></iris-icon>
          <span>back</span>
        </button>
      }
      @if (showCloseButton()) {
        <iris-icon-button
          class="close-button"
          data-test="guided-flow-close"
          icon="close"
          ariaLabel="Close provider finder"
          [type]="irisIconButtonTypes.TERTIARY"
          (click)="onClose()"
        ></iris-icon-button>
      }
    </div>
  }

  @if (!hasResults()) {
    <div class="content zero-results" data-test="guided-flow-zero-results" aria-live="polite">
      <div class="question">
        <h2 #zeroResultsHeading class="question-title" tabindex="-1">No exact matches.</h2>
        @if (appliedFilterChips().length) {
          <p class="question-subtitle">
            No providers fit every preference you selected. Try de-selecting some of your
            filters below to see more results.
          </p>
        } @else {
          <p class="question-subtitle">
            No providers are available for your current search. Try adjusting your filters or
            call us for help finding a match.
          </p>
        }
      </div>

      @if (appliedFilterChips().length) {
        <div class="chip-container" role="group" aria-label="Applied filters">
          @for (chip of appliedFilterChips(); track chip.formKey + ':' + chip.tag) {
            <button
              type="button"
              class="chip selected"
              data-test="guided-flow-applied-filter-chip"
              [attr.aria-label]="'Remove filter ' + chip.title"
              (click)="onRemoveChip(chip)"
            >
              <span>{{ chip.title }}</span>
              <iris-icon name="check" [size]="iconSizes.SM"></iris-icon>
            </button>
          }
        </div>
      }
    </div>
  } @else if (isComplete()) {
    @if (showFeedbackConfirm()) {
      <div class="content feedback" data-test="guided-flow-feedback-confirm" aria-live="polite">
        <div class="question">
          <h2 #feedbackConfirmHeading class="question-title" tabindex="-1">
            You're ready for the next step.
          </h2>
          <p class="question-subtitle">
            You've found your provider! Click on a time slot to schedule your first session.
          </p>
        </div>

        <div class="footer below-content">
          <span class="footer-icon">
            <iris-icon name="call" [size]="iconSizes.SM"></iris-icon>
          </span>
          <div class="footer-text">
            <p class="footer-title">Or, call for more support</p>
            <a class="footer-phone" [href]="'tel:' + supportPhoneLink">{{ supportPhoneDisplay }}</a>
          </div>
        </div>
      </div>
    } @else if (showFeedbackDeselect()) {
      <div class="content feedback" data-test="guided-flow-feedback-deselect" aria-live="polite">
        <div class="question">
          <h2 #feedbackDeselectHeading class="question-title" tabindex="-1">
            Let's keep searching.
          </h2>
          <p class="question-subtitle">
            To see more results, try removing some filters or start over to clear them all.
          </p>
        </div>

        @if (appliedFilterChips().length) {
          <div class="chip-container" role="group" aria-label="Applied filters">
            @for (chip of appliedFilterChips(); track chip.formKey + ':' + chip.tag) {
              <button
                type="button"
                class="chip selected"
                data-test="guided-flow-applied-filter-chip"
                [attr.aria-label]="'Remove filter ' + chip.title"
                (click)="onRemoveChip(chip)"
              >
                <span>{{ chip.title }}</span>
                <iris-icon name="check" [size]="iconSizes.SM"></iris-icon>
              </button>
            }
          </div>
        }

        <button
          type="button"
          class="iris-button iris-button--secondary start-over-button"
          data-test="guided-flow-start-over"
          (click)="onStartOver()"
        >
          Start over
        </button>

        <div class="footer below-content">
          <span class="footer-icon">
            <iris-icon name="call" [size]="iconSizes.SM"></iris-icon>
          </span>
          <div class="footer-text">
            <p class="footer-title">For more support, call a matching specialist at</p>
            <a class="footer-phone" [href]="'tel:' + supportPhoneLink">{{ supportPhoneDisplay }}</a>
          </div>
        </div>
      </div>
    } @else {
      <div class="content feedback" data-test="guided-flow-feedback" aria-live="polite">
        <div class="question">
          <h2 #feedbackHeading class="question-title" tabindex="-1">
            Did you find what you're looking for?
          </h2>
          @if (appliedFilterSummary()) {
            <p class="question-subtitle">
              You're seeing top recommended providers who match {{ appliedFilterSummary() }}.
            </p>
          }
        </div>

        <div class="feedback-actions" role="group" aria-label="Did you find what you're looking for?">
          <button
            type="button"
            class="feedback-thumb"
            data-test="guided-flow-feedback-up"
            aria-label="Yes, I found what I'm looking for"
            (click)="onFeedback(true)"
          >
            <span class="feedback-thumb-icon">
              <iris-icon name="thumb_up_outline" [size]="iconSizes.SM"></iris-icon>
            </span>
            <span class="feedback-thumb-label">Yes</span>
          </button>
          <button
            type="button"
            class="feedback-thumb"
            data-test="guided-flow-feedback-down"
            aria-label="No, I didn't find what I'm looking for"
            (click)="onFeedback(false)"
          >
            <span class="feedback-thumb-icon">
              <iris-icon name="thumb_down_outline" [size]="iconSizes.SM"></iris-icon>
            </span>
            <span class="feedback-thumb-label">No</span>
          </button>
        </div>
      </div>

      <div class="footer">
        <span class="footer-icon">
          <iris-icon name="call" [size]="iconSizes.SM"></iris-icon>
        </span>
        <div class="footer-text">
          <p class="footer-title">Looking for more guidance?</p>
          <p class="footer-phone">
            Call a matching specialist at
            <a [href]="'tel:' + supportPhoneLink">{{ supportPhoneDisplay }}</a>
          </p>
        </div>
      </div>
    }
  } @else {
  <div class="content" data-test="guided-flow-questionnaire" aria-live="polite">
    @if (currentStep(); as step) {
      <div class="question">
        <h2 #questionHeading class="question-title" data-test="guided-flow-question-title" tabindex="-1">{{ step.title }}</h2>
        @if (step.subtitle) {
          <p class="question-subtitle">{{ step.subtitle }}</p>
        }
      </div>

      <div class="chip-container" role="group" [attr.aria-label]="step.title">
        @for (chip of step.chips; track chip.title) {
          @let selected = isChipSelected(chip);
          <button
            type="button"
            class="chip"
            data-test="guided-flow-chip"
            [attr.data-test-chip]="chip.title"
            [class.selected]="selected"
            [attr.aria-pressed]="selected"
            (click)="toggleChip(chip)"
          >
            <span>{{ chip.title }}</span>
            @if (selected) {
              <iris-icon name="check" [size]="iconSizes.SM"></iris-icon>
            }
          </button>
        }
      </div>

      <button
        type="button"
        class="iris-button iris-button--primary next-button"
        data-test="guided-flow-next"
        (click)="onNext()"
      >
        Next
      </button>
    }
  </div>
  }

  @if (!hasResults() || !isComplete()) {
    <div class="footer">
      <span class="footer-icon">
        <iris-icon name="call" [size]="iconSizes.SM"></iris-icon>
      </span>
      <div class="footer-text">
        <p class="footer-title">Looking for more guidance?</p>
        <p class="footer-subtitle">
          Call a matching specialist at
          <a [href]="'tel:' + supportPhoneLink">{{ supportPhoneDisplay }}</a>
        </p>
      </div>
    </div>
  }
</div>
